import { useFormikContext } from 'formik';
import moment from 'moment';
import React, { useMemo, useEffect, useState } from 'react';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { getClosingDatesRanges } from 'Store/calendar/calendar.helper';
import { ICONS } from 'Components/utils/icon/icons';
import { BucketFormikInput as Field, type BucketFormikInputProps } from 'Src/types/forms';
import { Notification } from 'Components/frameworks/notification/Notification';
import { Title } from 'Components/frameworks/title/Title';
import { TabLikeFilters } from 'Components/frameworks/formik/TabLikeFilters';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { t } from 'Src/i18n';
import type {
  FormFieldOption,
  FormProduct,
  ProductOption,
  FormProducts,
  ProductFilter,
} from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { filterProductsByType } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import type { FormCompanion, FormValues } from './PrebookingForm.utils';
import { getKidFutureBithdateValidator } from './PrebookingForm.utils';
export type PrebookingFormProjectProps = {
  adultsOptions: Array<FormFieldOption>;
  dateFormat: string;
  departureDateEnd: Date;
  departureDateStart: Date;
  kidsOptions: Array<FormFieldOption>;
  productOptions: Array<ProductOption>;
  product: FormProduct | null;
  products: FormProducts;
  isDisabled?: boolean;
};
const DEFAULT_COMPANION: FormCompanion = {
  birthdate: null,
  civility: '',
  firstName: '',
  lastName: '',
};
export const PrebookingFormProject = ({
  adultsOptions,
  dateFormat,
  departureDateEnd,
  departureDateStart,
  kidsOptions,
  productOptions,
  product,
  products,
  isDisabled,
}: PrebookingFormProjectProps) => {
  const { values, setValues, setFieldValue } = useFormikContext<FormValues>();
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const transportFeatureFlip = isFeatureFlipped(appContext)('prebooking.transport');
  const showFiltersSunSnow = isFeatureFlipped(appContext)('prebooking.showFiltersSunSnow');
  const { productId, adults, adultsNumber, kids, kidsNumber } = values;
  // Local state for filtering UI (not sent with form)
  const [productFilterType, setProductFilterType] = useState<ProductFilter | null>(null);
  const filteredProductOptions = useMemo(() => {
    if (!showFiltersSunSnow || !productFilterType) {
      return productOptions;
    }

    // Flow refinement: productFilterType is ProductFilter at this point
    const filter: ProductFilter = productFilterType as any;
    const filteredProducts = filterProductsByType(products, filter);
    const filteredProductIds = Object.keys(filteredProducts);
    return productOptions.filter(
      (option) => option.value === null || filteredProductIds.includes(option.value),
    );
  }, [showFiltersSunSnow, productFilterType, productOptions, products]);
  useEffect(() => {
    if (showFiltersSunSnow && productId && productFilterType) {
      const currentProduct = products[productId];
      const filter: ProductFilter = productFilterType as any;

      if (
        (filter === 'sun' && !currentProduct?.sun) ||
        (filter === 'snow' && !currentProduct?.snow)
      ) {
        setFieldValue('productId', '');
      }
    }
  }, [showFiltersSunSnow, productFilterType, productId, products, setFieldValue]);
  const minAuthorizedAge = product ? product.minAuthorizedAge : undefined;
  const isSki = product ? product.isSki : undefined;
  const isSAFC = product ? product.productId === 'SAFC' : undefined;
  const isCOLC = product ? product.productId === 'COLC' : undefined;
  const isCSCK = product ? product.productId === 'CSKC' : undefined;
  const isBOKC = product ? product.productId === 'BOKC' : undefined;
  const minAuthorizedAgeInMonths = product ? product.minAuthorizedAgeInMonths : 0;
  const durationOptions = product ? product.durations : [];
  const transportOptions = product ? product.transports : [];
  const accommodationCategoryOptions = product ? product.accommodations : [];
  const adultsInt = parseInt(adultsNumber, 10);
  const kidsInt = parseInt(kidsNumber, 10);

  const handleAdultsNumber = (e) => {
    const updatedAdultsNumber = e.target.value;
    const updatedAdultsInt = parseInt(updatedAdultsNumber, 10);
    const newAdults = updatedAdultsInt - adultsInt;
    const updatedAdults =
      newAdults > 0
        ? [
            ...adults,
            ...Array(newAdults)
              .fill(null)
              .map(() => DEFAULT_COMPANION),
          ]
        : adults.slice(0, updatedAdultsInt - 1);
    setValues({ ...values, adultsNumber: updatedAdultsNumber, adults: updatedAdults });
  };

  const handleKidsNumber = (e) => {
    const updatedKidsNumber = e.target.value;
    const updatedKidsInt = parseInt(updatedKidsNumber, 10);
    const newKids = updatedKidsInt - kidsInt;
    const updatedKids =
      newKids > 0
        ? [
            ...kids,
            ...Array(newKids)
              .fill(null)
              .map(() => DEFAULT_COMPANION),
          ]
        : kids.slice(0, updatedKidsInt);
    setValues({ ...values, kidsNumber: updatedKidsNumber, kids: updatedKids });
  };

  const sortedOpeningDates = [...(product?.openingDates || [])].sort(
    (date1, date2) => Number(date1.opening) - Number(date2.opening),
  );
  const firstOpeningDate = sortedOpeningDates[0]?.opening;
  const lastClosingDate = sortedOpeningDates[sortedOpeningDates.length - 1]?.closing;
  // Use product-specific departureDateStart if available, otherwise use global departureDateStart
  const productDepartureDateStart = product?.departureDateStart;
  const hasProductSpecificStartDate =
    productDepartureDateStart && moment(productDepartureDateStart, 'YYYY-MM-DD').isValid();
  const effectiveDepartureDateStart = hasProductSpecificStartDate
    ? moment(productDepartureDateStart, 'YYYY-MM-DD').toDate()
    : departureDateStart;
  // Use product-specific departureDateEnd if available (absolute), otherwise use global departureDateEnd with API comparison
  const productDepartureDateEnd = product?.departureDateEnd;
  const hasProductSpecificEndDate =
    productDepartureDateEnd && moment(productDepartureDateEnd, 'YYYY-MM-DD').isValid();
  // Calculate disabled days - if product has specific dates, don't disable days within that range
  let disabledDays = getClosingDatesRanges(sortedOpeningDates);

  // If product has specific end date, remove the last closing range that would block dates after lastClosingDate
  if (hasProductSpecificEndDate && disabledDays.length > 0) {
    // Remove the last disabled range (which blocks dates after the last closing date)
    disabledDays = disabledDays.slice(0, -1);
  }

  let minDate;

  if (hasProductSpecificStartDate) {
    minDate = effectiveDepartureDateStart;
  } else if (
    firstOpeningDate &&
    momentFromApiDate(firstOpeningDate).isAfter(moment(effectiveDepartureDateStart))
  ) {
    minDate = momentFromApiDate(firstOpeningDate).toDate();
  } else {
    minDate = effectiveDepartureDateStart;
  }

  // If product has a specific departureDateEnd, use it as absolute date (no comparison with API dates)
  // Otherwise, use global departureDateEnd but limit it to lastClosingDate from API if needed
  let maxDate;

  if (hasProductSpecificEndDate) {
    maxDate = moment(productDepartureDateEnd, 'YYYY-MM-DD').toDate();
  } else if (
    lastClosingDate &&
    moment(departureDateEnd).isAfter(momentFromApiDate(lastClosingDate))
  ) {
    maxDate = momentFromApiDate(lastClosingDate).toDate();
  } else {
    maxDate = departureDateEnd;
  }

  const isClosed = !!lastClosingDate && momentFromApiDate(lastClosingDate).isBefore(moment());
  const filterOptions = [
    {
      value: 'sun',
      label: t('prebookingForm.stay.filterSun', {
        defaultValue: 'Sun',
      }),
    },
    {
      value: 'snow',
      label: t('prebookingForm.stay.filterSnow', {
        defaultValue: 'Snow',
      }),
    },
  ];

  const handleFilterChange = (value: string | null) => {
    setProductFilterType(value as ProductFilter | null);

    // Track filter click when a filter is selected
    if (value) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FILTERS, {
        detail_click: value,
      });
    }
  };

  return (
    <div id="pbf-step-0" data-testid="prebooking-form-form-project">
      {showFiltersSunSnow && (
        <React.Fragment>
          <h3 className="mb-10 mt-20 px-10 text-18 font-bold">
            {t('prebookingForm.stay.filterTitle', {
              defaultValue: 'Type of destination',
            })}
          </h3>
          <TabLikeFilters
            options={filterOptions}
            selectedValue={productFilterType}
            onChange={handleFilterChange}
            dataTestId="product-filter-tabs"
            additionalClassNames="w-full md:w-1/2"
          />
        </React.Fragment>
      )}

      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4" id="pbf-step-0-title">
        {t('prebookingForm.stay.title')}
      </Title>
      <p className="m-0 mb-10 px-10 text-12">{t('prebookingForm.mandatoryFieldsInformation')}</p>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="product-id-field"
            hideErrorMessage
            isRequired
            label={t('prebookingForm.stay.productLabel')}
            name="productId"
            options={filteredProductOptions}
            placeholder={t('prebookingForm.stay.productPlaceholder')}
            type="select"
            isDisabled={isDisabled}
          />
          {minAuthorizedAge ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={minAuthorizedAge}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
          {isSki ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={t('prebookingForm.stay.dateSkiResortInformation')}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
          {isSAFC ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={t('prebookingForm.stay.safcResortInformation')}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
          {isCOLC ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={t('prebookingForm.stay.colcResortInformation')}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
          {isCSCK ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={t('prebookingForm.stay.cskcResortInformation')}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
          {isBOKC ? (
            <Notification
              className="mt-15 rounded-12 px-20 py-0"
              text={t('prebookingForm.stay.bokcResortInformation')}
              iconName={ICONS.EXCLAMATION_POINT}
            />
          ) : null}
        </div>
        {transportFeatureFlip && (
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="departure-city-field"
              hideErrorMessage
              isDisabled={!productId || isDisabled}
              isRequired
              label={t('prebookingForm.stay.departureCityLabel')}
              name="departureCity"
              options={transportOptions}
              placeholder={t('prebookingForm.stay.departureCityPlaceholder')}
              type="select"
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <Field
            className="z-1"
            dataTestId="departure-date-field"
            dateFormat={dateFormat}
            hideErrorMessage
            isDisabled={!productId || isClosed || isDisabled}
            isRequired
            label={t('prebookingForm.stay.departureDateLabel')}
            maxDate={maxDate}
            minDate={minDate}
            disabledDays={disabledDays as unknown as BucketFormikInputProps['disabledDays']}
            name="departureDate"
            placeholder={t('prebookingForm.stay.departureDatePlaceholder')}
            type="datepicker"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="duration-field"
            hideErrorMessage
            isDisabled={!productId || isDisabled}
            isRequired
            label={t('prebookingForm.stay.durationLabel')}
            name="duration"
            options={durationOptions}
            placeholder={t('prebookingForm.stay.durationPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="accommodation-category-field"
            label={t('prebookingForm.stay.accommodationsLabel')}
            hideErrorMessage
            isDisabled={!productId || isDisabled}
            isRequired
            name="accommodationCategory"
            options={accommodationCategoryOptions}
            placeholder={t('prebookingForm.stay.accommodationsCategoryPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="adults-number-field"
            label={t('prebookingForm.stay.numberOfAdultsLabel')}
            isRequired
            hideErrorMessage
            name="adultsNumber"
            options={adultsOptions}
            onChange={handleAdultsNumber}
            type="select"
            isDisabled={isDisabled}
          />
        </div>
      </div>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="kids-number-field"
            label={t('prebookingForm.stay.numberOfKidsLabel')}
            hideErrorMessage
            isRequired
            name="kidsNumber"
            onChange={handleKidsNumber}
            options={kidsOptions}
            type="select"
            isDisabled={isDisabled}
          />
        </div>
      </div>
      {kids.length > 0 && (
        <div className="flex flex-wrap">
          {kids.map((_kid, index) => (
            <div key={`kid-${index}`} className="w-full p-10 md:w-1/2">
              <Field
                dataTestId={`kids-${index}-birthdate-field`}
                hideErrorMessage={!minAuthorizedAge}
                isRequired
                label={t('prebookingForm.pax.childBirthDateInformationLabel_n', {
                  children: index + 1,
                })}
                name={`kids.${index}.birthdate`}
                placeholder={t('prebookingForm.mainTraveler.birthDateInformation')}
                type="birthdate"
                dayPlaceholder="DD"
                monthPlaceholder="MM"
                yearPlaceholder="YYYY"
                dateFormat={dateFormat}
                validate={getKidFutureBithdateValidator(minAuthorizedAgeInMonths, minAuthorizedAge)}
                isDisabled={isDisabled}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
